<template>
  <div class="flex flex-col gap-3">
    <BaseSection heading="Gerätedaten">
      <RecordDeviceDetails v-model:device="device" :validation="v$.device" />
    </BaseSection>

    <BaseInformation
      v-if="!isCounterPressureValid"
      :status="Status.ERROR"
      information-text="Der Gegendruck des Ventils ist zu hoch."
    >
      <template #button>
        <TertiaryButton
          data-testId="button_switchToNoMounting"
          icon="mdi-arrow-right"
          label="Keine Montage auswählen"
          @click="switchToNoMounting"
        />
      </template>
    </BaseInformation>

    <BaseSection heading="Batterieeinheit">
      <BatteryPackDetails v-model="batteryPack" :validation="v$.batteryPack" />
    </BaseSection>

    <BaseSection
      heading="Adapter"
      :required="false"
      :label="amountOfAdapters.toString()"
    >
      <AccessoryList
        ref="accessoryComponentRef"
        v-model="installationPoint"
        @on-adapter-change="onAdapterChange"
      />
    </BaseSection>

    <BaseSection
      v-if="!isTkdReplaceAction"
      heading="Heizkörper"
      :required="false"
    >
      <RadiatorSelector
        v-model:radiator-id="installationPoint.radiatorId"
        :installation-point="installationPoint"
        :is-edit-mode="false"
        :is-srt-device="true"
        :radiators-list="radiators"
      />
    </BaseSection>

    <BaseSection
      v-if="!isTkdReplaceAction"
      heading="Verortung"
      :required="false"
    >
      <DeviceGroup
        v-model:device="device"
        v-model:device-event="deviceEvent"
        v-model:installation-point="installationPoint"
      />
    </BaseSection>

    <BaseSection
      v-if="!isTkdReplaceAction"
      heading="Weitere Angaben"
      :required="false"
    >
      <AdditionalInfo v-model:device="device" />
      <div class="md:flex">
        <DateMount
          v-model:device-event="deviceEvent"
          :validation="v$.deviceEvent"
          class="md:basis-1/2 md:pr-2"
        />
      </div>
      <AutomatedMeterReadingSelector v-model:device="device" />
    </BaseSection>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, inject, provide, ref, Ref } from 'vue';
import RadiatorSelector from '../RadiatorSelector/RadiatorSelector.vue';
import AdditionalInfo from '../AdditionalInfo.vue';
import DeviceGroup from '../AdvancedRegistraionMeter/DeviceGroup.vue';
import AutomatedMeterReadingSelector from '@/components/Forms/DeviceFormComponents/AutomatedMeterReadingSelector.vue';
import RecordDeviceDetails from '@/components/Forms/DeviceFormComponents/RecordDeviceDetails.vue';
import DateMount from '@/components/Forms/DeviceFormComponents/DateMount.vue';
import { useValidation } from '@/composables/useValidation';
import useStores from '@/composables/useStores';
import { Room } from '@/models';
import { Radiator } from '@/models/radiators/Radiator';
import { SmartRadiatorThermostat } from '@/models/devices/Device';
import {
  FailedInstallationAttempt,
  InstallationPoint,
} from '@/models/installationPoint/InstallationPoint';
import { DeviceEventParams } from '@/models/installationPoint/DeviceEvent';
import { radiatorTypes } from '@/enums/radiator/generic';
import { createRadiator } from '@/models/radiators';
import BatteryPackDetails from '@/components/Forms/DeviceFormComponents/BatteryPackDetails.vue';
import AccessoryList from '@/components/Forms/DeviceFormComponents/AccessoryList.vue';
import {
  AccessoryType,
  deviceTypes,
  notMountedReasonsListSRT,
} from '@/enums/device';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import { useOrderStore } from '@/store/order/orderStore';
import { ImageCaptureRef } from '@/components/Forms/DeviceFormComponents/ImageCapture.vue';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import { Status } from '@/enums/generic';
import TertiaryButton from '@/components/UI/Button/TertiaryButton.vue';
import { FormAction, useFormStore } from '@/store/form/formStore';
import { BatteryPack } from '@/models/installationPoint/Accessory';

const { radiatorStore, entitiesStore, formStore } = useStores();

const props = defineProps<{
  device: SmartRadiatorThermostat;
  deviceEvent: DeviceEventParams;
  installationPoint: InstallationPoint;
  // mass exchange
  newBatteryPack: BatteryPack;
}>();

const imageCaptureRef: Ref<ImageCaptureRef | null> = ref(null);

const emit = defineEmits(['update-entity']);

const device = computed(() => {
  return props.device;
});

const deviceEvent = computed(() => {
  return props.deviceEvent;
});

const installationPoint = computed(() => {
  return props.installationPoint;
});

const batteryPack = computed(() => {
  if (useFormStore().isReplaceFormAction()) {
    return props.newBatteryPack;
  }
  return props.installationPoint.accessories.find(
    (accessory) =>
      accessory.type === AccessoryType.SRT_BATTERY_PACK &&
      !accessory.unmountedAt
  );
});

const amountOfAdapters = computed(() => {
  return installationPoint.value.accessories.filter(
    (accessory) =>
      accessory.type === AccessoryType.SRT_ADAPTER && !accessory.unmountedAt
  ).length;
});

const onAdapterChange = () => {
  device.value.counterPressure = undefined;
};

const radiators = computed(() => {
  let parentId = formStore.currentForm.parentId as string;
  if (entitiesStore.hasEntityById(parentId)) {
    const parent: Room | Radiator = entitiesStore.getEntityById(parentId);
    if (parent instanceof Radiator) {
      parentId = parent.getParentId() as string;
    }

    return radiatorStore
      .getAllRadiatorsInRoom(parentId)
      .filter((radiator) => {
        return (
          useStores().deviceStore.getDevicesOnRadiator(
            radiator.id,
            deviceTypes.SmartRadiatorThermostat
          ).length === 0
        );
      })
      .sort((a: Radiator, b: Radiator) => a.ordinal - b.ordinal);
  }
  return [];
});

const isMounted = inject('isMounted') as Ref<boolean>;
const isOnSiteInspection = computed(() => {
  return useOrderStore().isOnSiteInspection();
});

const failedInstallationAttempt = inject(
  'failedInstallationAttempt'
) as Ref<FailedInstallationAttempt>;

const switchToNoMounting = () => {
  isMounted.value = false;
  failedInstallationAttempt.value = new FailedInstallationAttempt();
  failedInstallationAttempt.value.reason = notMountedReasonsListSRT[1].value;
  failedInstallationAttempt.value.note = `Gegendruck: ${device.value.counterPressure}`;
};

const createPlaceholderRadiator = () => {
  const placeholderRadiator = createRadiator(radiatorTypes.PlaceholderRadiator);
  placeholderRadiator.parentRoomId = installationPoint.value.getParentId();
  placeholderRadiator.ordinal = radiatorStore.getNewRadiatorOrdinal(
    placeholderRadiator.parentRoomId
  );
  return placeholderRadiator;
};

const saveRadiator = () => {
  if (!installationPoint.value.radiatorId && !isOnSiteInspection.value) {
    const placeholderRadiator = createPlaceholderRadiator();
    if (placeholderRadiator) {
      installationPoint.value.radiatorId = placeholderRadiator.id;
    }
    entitiesStore.saveEntity(placeholderRadiator);
  }
  emit('update-entity', installationPoint.value);
};

const isCounterPressureValid = computed(() => {
  return !device.value.counterPressure || device.value.counterPressure <= 90;
});

const isTkdReplaceAction = computed(() => {
  return useFormStore().formAction === FormAction.REPLACE;
});

type AccessoryComponentRef = {
  validateAdapters: () => void;
};
const accessoryComponentRef: Ref<AccessoryComponentRef | null> = ref(null);

const { v$, isFormValid } = useValidation(
  { device, batteryPack },
  { isOnSiteInspection: isOnSiteInspection.value }
);

defineExpose({
  isFormValid: computed(
    () =>
      isFormValid.value &&
      (isOnSiteInspection.value ? imageCaptureRef.value?.hasImage : true)
  ),
  saveRadiator,
  saveImage: () => imageCaptureRef.value?.saveImage(),
  accessoryComponentRef,
});
</script>
